/**
 * Client-side "display currency" converter - Vastradevi Theme.
 *
 * Shopify's own currencies.js (https://cdn.shopify.com/s/javascripts/
 * currencies.js) only exposes a static exchange-rate table plus
 * Currency.convert(amount, from, to) - it does not touch the DOM or know
 * this shop's money formats. This file adds that layer: it formats/converts
 * prices for display and overrides window.Shopify.formatMoney, which every
 * dynamic price renderer already in this theme (cart drawer, cart page,
 * quickview, product cards, swatches) calls through - so they all pick up
 * the selected currency for free.
 *
 * The rate table itself is fetched at most once an hour: it's cached in
 * localStorage with a timestamp, so most page loads reuse the cached rates
 * instead of re-requesting the CDN script.
 *
 * This is DISPLAY-ONLY. The shop still charges in window.VDShopCurrency at
 * checkout - real multi-currency checkout requires Shopify Markets. The
 * chosen currency is remembered in localStorage.
 */
(function () {
  'use strict';

  var STORAGE_KEY = 'vd_display_currency';
  var RATES_CACHE_KEY = 'vd_currency_rates_v1';
  var RATES_CACHE_TTL_MS = 60 * 60 * 1000; // 1 hour
  var shopCurrency = (window.VDShopCurrency || 'INR').toUpperCase();

  var CURRENCIES = {
    INR: { symbol: '₹', locale: 'en-IN' },
    USD: { symbol: '$', locale: 'en-US' },
    EUR: { symbol: '€', locale: 'en-US' },
    GBP: { symbol: '£', locale: 'en-GB' },
    AUD: { symbol: 'A$', locale: 'en-US' },
    CAD: { symbol: 'C$', locale: 'en-US' },
    AED: { symbol: 'AED ', locale: 'en-US' },
    SGD: { symbol: 'S$', locale: 'en-US' }
  };

  // Shop's base currency might not be one of the 8 common ones above -
  // still needs a display entry so formatMoney always has somewhere to fall
  // back to when a target currency's rate isn't available yet.
  if (!CURRENCIES[shopCurrency]) {
    CURRENCIES[shopCurrency] = { symbol: shopCurrency + ' ', locale: 'en-US' };
  }

  function readStoredCurrency() {
    try {
      var stored = window.localStorage.getItem(STORAGE_KEY);
      if (stored && CURRENCIES[stored]) return stored;
    } catch (e) {}
    return shopCurrency;
  }

  var state = { current: readStoredCurrency() };

  function formatMoney(cents, code) {
    code = (code && CURRENCIES[code]) ? code : state.current;
    var amount = cents / 100;

    if (code !== shopCurrency) {
      var rates = window.Currency && window.Currency.rates;
      if (rates && rates[shopCurrency] && rates[code]) {
        amount = window.Currency.convert(amount, shopCurrency, code);
      } else {
        // Rates not loaded (yet) or currency unsupported - show the real
        // base-currency price rather than a wrong/unconverted number.
        code = shopCurrency;
      }
    }

    var meta = CURRENCIES[code];
    var formatted = amount.toLocaleString(meta.locale, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
    return meta.symbol + formatted;
  }

  // Converts a major-unit amount (rupees, not paise/cents) from the shop's
  // base currency to the given/current display currency. Used by things
  // that work in major units already, e.g. the collection price-range
  // filter's slider/number inputs. Falls back to the input amount unchanged
  // if rates aren't ready or the currency has no rate.
  function convertAmount(amount, code) {
    code = (code && CURRENCIES[code]) ? code : state.current;
    if (code === shopCurrency) return amount;
    var rates = window.Currency && window.Currency.rates;
    if (rates && rates[shopCurrency] && rates[code]) {
      return window.Currency.convert(amount, shopCurrency, code);
    }
    return amount;
  }

  // Inverse of convertAmount: takes a major-unit amount already expressed in
  // the given/current display currency and converts it back to the shop's
  // base currency (e.g. before submitting a price filter to Shopify, which
  // only understands the shop's own currency).
  function convertToShop(amount, code) {
    code = (code && CURRENCIES[code]) ? code : state.current;
    if (code === shopCurrency) return amount;
    var rates = window.Currency && window.Currency.rates;
    if (rates && rates[shopCurrency] && rates[code]) {
      return window.Currency.convert(amount, code, shopCurrency);
    }
    return amount;
  }

  function symbolFor(code) {
    code = (code && CURRENCIES[code]) ? code : state.current;
    return CURRENCIES[code].symbol.trim();
  }

  function localeFor(code) {
    code = (code && CURRENCIES[code]) ? code : state.current;
    return CURRENCIES[code].locale;
  }

  function refreshStaticPrices() {
    document.querySelectorAll('[data-money]').forEach(function (el) {
      var cents = parseFloat(el.getAttribute('data-money'));
      if (isNaN(cents)) return;
      var prefix = el.getAttribute('data-money-prefix') || '';
      el.textContent = prefix + formatMoney(cents);
    });
  }

  function updateSwitcherUI(code) {
    document.querySelectorAll('.currency-switcher').forEach(function (sw) {
      var options = sw.querySelectorAll('.currency-switcher__option');
      var activeOption = null;
      options.forEach(function (opt) {
        var isActive = opt.getAttribute('data-currency') === code;
        opt.classList.toggle('is-active', isActive);
        opt.setAttribute('aria-selected', isActive ? 'true' : 'false');
        if (isActive) activeOption = opt;
      });

      var currentLabel = sw.querySelector('.currency-switcher__current');
      if (currentLabel) currentLabel.textContent = CURRENCIES[code].symbol.trim() + ' ' + code;

      // Reuse the already-rendered flag markup from the matching menu option
      // instead of maintaining a second copy of every flag SVG in JS.
      var toggleFlag = sw.querySelector('.currency-switcher__toggle .cur-flag');
      var optionFlag = activeOption ? activeOption.querySelector('.cur-flag') : null;
      if (toggleFlag && optionFlag) toggleFlag.outerHTML = optionFlag.outerHTML;
    });
  }

  function setCurrency(code) {
    code = (code || '').toUpperCase();
    if (!CURRENCIES[code] || code === state.current) return;
    state.current = code;
    try { window.localStorage.setItem(STORAGE_KEY, code); } catch (e) {}
    updateSwitcherUI(code);
    refreshStaticPrices();
    document.dispatchEvent(new CustomEvent('vd:currency-change', { detail: { currency: code } }));
  }

  window.Shopify = window.Shopify || {};
  window.Shopify.formatMoney = formatMoney;

  window.VDCurrency = {
    list: Object.keys(CURRENCIES),
    shopCurrency: shopCurrency,
    getCurrent: function () { return state.current; },
    setCurrency: setCurrency,
    formatMoney: formatMoney,
    convertAmount: convertAmount,
    convertToShop: convertToShop,
    symbolFor: symbolFor,
    localeFor: localeFor,
    refresh: refreshStaticPrices
  };

  // Loads Currency.rates from a cached localStorage copy (if under an hour
  // old), otherwise fetches the CDN script fresh and re-caches it.
  function loadRates(done) {
    try {
      var cached = JSON.parse(window.localStorage.getItem(RATES_CACHE_KEY) || 'null');
      if (cached && cached.rates && (Date.now() - cached.ts) < RATES_CACHE_TTL_MS) {
        window.Currency = {
          rates: cached.rates,
          convert: function (amount, from, to) { return amount * this.rates[from] / this.rates[to]; }
        };
        done();
        return;
      }
    } catch (e) {}

    var script = document.createElement('script');
    script.src = 'https://cdn.shopify.com/s/javascripts/currencies.js';
    script.onload = function () {
      try {
        if (window.Currency && window.Currency.rates) {
          window.localStorage.setItem(RATES_CACHE_KEY, JSON.stringify({ rates: window.Currency.rates, ts: Date.now() }));
        }
      } catch (e) {}
      done();
    };
    script.onerror = done;
    document.head.appendChild(script);
  }

  function boot() {
    refreshStaticPrices();
    updateSwitcherUI(state.current);

    // Catches priced content inserted after boot (infinite scroll, wishlist
    // reveal, recently-viewed rebuilds) that renders with the shop's base
    // currency text baked in server-side or from a stale formatMoney call.
    var observer = new MutationObserver(function (mutations) {
      var hasPricedNode = mutations.some(function (m) {
        return Array.prototype.some.call(m.addedNodes, function (node) {
          if (node.nodeType !== 1) return false;
          return node.hasAttribute('data-money') || !!node.querySelector('[data-money]');
        });
      });
      if (hasPricedNode) refreshStaticPrices();
    });
    observer.observe(document.body, { childList: true, subtree: true });

    loadRates(function () {
      // Rates may have just become available (or changed) after the first
      // paint above - repaint anything showing a non-base currency, and let
      // other scripts (e.g. the collection price-range filter) know too.
      refreshStaticPrices();
      document.dispatchEvent(new CustomEvent('vd:currency-change', { detail: { currency: state.current } }));
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
